import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../../state'
import * as dimensionFilter from '../../../../shared/dimension-filter'

/**
 * Record filters backed by the records Current List draft parameters
 */
@ngCore.Component({
  host: {
    // TODO: Once this component is reused, this can be removed.
    'data-atn-space-variant': 'full',
  },
  imports: [
    dimensionFilter.Component,
    ds.button.Component,
    ds.input.number.Component,
    ds.input.select.multiple.Component,
    ds.overlay.slideout.Component,
  ],
  selector: 'atn-space-record-filters',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** Records collection that owns the filter parameters and plugins. */
  public readonly recordsEc = ngCore.inject(state.space.full.records.Collection)

  /** Users collection used only to resolve Last Updated By options. */
  private readonly _usersEc = ngCore.inject(
    state.space.userAccess.users.Collection,
  )

  /** Current List plugin that owns the edited record filters. */
  public readonly currentListPlugin = this.recordsEc.plugins.currentListPlugin

  /** Current draft filters displayed by the controls. */
  public readonly editedFiltersSg =
    this.currentListPlugin.editedParams.selectors.filters.asSignal

  /** Space dimension-filter plugin owned by the records collection. */
  public readonly spaceDimensionFilterPlugin = this.recordsEc.plugins
    .lookup(state.space.full.records.plugins.spaceDimensionFilter.Plugin)
    .get()

  /** Room Type dimension-filter plugin owned by the records collection. */
  public readonly roomTypeDimensionFilterPlugin = this.recordsEc.plugins
    .lookup(state.space.full.records.plugins.roomTypeDimensionFilter.Plugin)
    .get()

  /** Organization dimension-filter plugin owned by the records collection. */
  public readonly organizationDimensionFilterPlugin = this.recordsEc.plugins
    .lookup(state.space.full.records.plugins.organizationDimensionFilter.Plugin)
    .get()

  /** Cluster dimension-filter plugin owned by the records collection. */
  public readonly clusterDimensionFilterPlugin = this.recordsEc.plugins
    .lookup(state.space.full.records.plugins.clusterDimensionFilter.Plugin)
    .get()

  /** Survey Status options for the multi-select. */
  public readonly statusOptions: ds.input.select.models.Item[] =
    ents.extra.space.SurveyStatus.ALL.map(status => ({
      label: status,
      value: status,
    }))

  /** User options for the Last Updated By multi-select. */
  public readonly userOptionsSg = ngCore.computed<
    ds.input.select.models.Item<ents.extra.space.User.Id>[]
  >(
    (
      users = this._usersEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
    ) => users.map(user => ({ label: user.name, value: user.id })),
  )

  /** Updates the draft minimum and maximum area filters. */
  public areaChanged(min?: number, max?: number): void {
    if (min !== undefined && max !== undefined && min > max) {
      ;[min, max] = [max, min]
    }

    this._setFilters('Space.RecordsList.Filters.Area.Changed', {
      sqftMax: max,
      sqftMin: min,
    })
  }

  /** Updates the draft Survey Status filter. */
  public statusesChanged(selectedValues: readonly unknown[]): void {
    this._setFilters('Space.RecordsList.Filters.Statuses.Changed', {
      surveyStatuses: selectedValues.length
        ? TypeAsserter.validateButJustWarn(
            selectedValues,
            TypeAsserter.Factory.from(
              TypeChecker.Factory.list(
                ents.extra.space.SurveyStatus.isSurveyStatus,
              ),
            ),
          )
        : undefined,
    })
  }

  /** Updates the draft Last Updated By filter. */
  public lastUpdatedByChanged(selectedValues: readonly unknown[]): void {
    const userIds = selectedValues.length
      ? TypeAsserter.validateButJustWarn(
          selectedValues,
          TypeAsserter.Factory.from(
            TypeChecker.Factory.list(ents.core.admin.User.Id.isId),
          ),
        )
      : undefined

    this._setFilters('Space.RecordsList.Filters.LastUpdatedBy.Changed', {
      lastUpdatedBy: userIds,
    })
  }

  /** Applies the Current List draft parameters and closes the slideout. */
  public applyClickHandler(): void {
    this.currentListPlugin.feature.actions.applyParams
      .from('Space.RecordsList.Filters.Apply.Clicked')
      .dispatch()
  }

  /** Discards the Current List draft parameters and closes the slideout. */
  public cancelClickHandler(): void {
    this.currentListPlugin.feature.actions.discardParams
      .from('Space.RecordsList.Filters.Cancel.Clicked')
      .dispatch()
  }

  /** Clears the Current List draft filters without closing the slideout. */
  public resetClickHandler(): void {
    this.currentListPlugin.editedParams.actions.setFilters
      .from('Space.RecordsList.Filters.Reset.Clicked')
      .dispatch({})
  }

  /** Merges simple control changes into the Current List draft filters. */
  private _setFilters(
    source: string,
    changes: Partial<ents.extra.space.Record.Filter>,
  ): void {
    this.currentListPlugin.editedParams.actions.setFilters
      .from(source)
      .dispatch({ ...this.editedFiltersSg(), ...changes })
  }
}
